<template>
    <div class="mb-8 grid border-b-2 p-3">
        <h3 class="mb-4 text-content-lg font-medium">Optional Flags</h3>
        <DsCheckbox
            v-model="showSelectAll"
            label="Show Select all option"
            name="showSelectAll"
            value="showSelectAll"
        />
        <DsCheckbox
            v-model="disableSelectAll"
            label="Disable Select all option"
            name="disableSelectAll"
            value="disableSelectAll"
        />
    </div>
    <div class="grid grid-cols-2 gap-8">
        <section>
            <h3 class="mb-4 text-content-lg font-medium">Non-Grouped</h3>
            <div class="flex flex-col gap-4">
                <h4 class="text-sm text-content-secondary">With Search</h4>
                <DsMultiSelect
                    v-model="selectedClients"
                    v-model:search="clientSearch"
                    id="clients"
                    :disableSelectAll
                    :error="errorClients"
                    :label="labelClients"
                    :options="clientOptions"
                    :placeholder="placeholderClients"
                    required
                    searchInputPlaceholder="Search clients"
                    :showSelectAll
                    @close="handleClientsClose"
                >
                    <template #optionLabel="{ option }: DsMultiSelectSlotProps<Client>">
                        <div class="flex flex-col">
                            <div class="flex items-center gap-2 break-all">
                                <span class="text-ds-primary-600">{{ option.role }}:</span>
                                <span class="font-medium">{{ option.name }}</span>
                            </div>
                            <div class="break-all text-sm text-ds-primary-600">{{ option.email }}, {{ option.phone }}</div>
                        </div>
                    </template>

                    <template #optionTrailing>
                        <DsButton
                            icon="fa-regular fa-pen"
                            size="sm"
                            variant="secondary"
                            @click="editClientDialog = true"
                        >
                            Edit
                        </DsButton>
                    </template>
                </DsMultiSelect>

                <h4 class="text-sm text-content-secondary">Without Search</h4>
                <DsMultiSelect
                    v-model="selectedDocuments"
                    id="documents"
                    :disableSelectAll
                    :error="errorDocuments"
                    :label="labelDocuments"
                    :options="documentOptions"
                    :placeholder="placeholderDocuments"
                    required
                    :showSelectAll
                    @close="handleDocumentsClose"
                >
                    <template #optionLabel="{ option }">
                        <div class="min-w-0">
                            <span class="break-all">{{ option.title }}</span>
                        </div>
                    </template>
                </DsMultiSelect>
            </div>
        </section>

        <section>
            <h3 class="mb-4 text-content-lg font-medium">Grouped</h3>
            <div class="flex flex-col gap-4">
                <h4 class="text-sm text-content-secondary">With Search</h4>
                <DsMultiSelect
                    v-model="selectedWireDocs"
                    v-model:search="documentsSearch"
                    id="wire-docs"
                    :disableSelectAll
                    groupLabel="branch"
                    helperText="This is helper text"
                    :label="labelDocuments"
                    :options="wireDocOptions"
                    :placeholder="placeholderDocuments"
                    :showSelectAll
                    @close="handleClose"
                    @open="handleOpen"
                    @toggleOption="handleToggleOption"
                    @update:search="handleDocumentSearch"
                >
                    <template #optionLabel="{ option }: { option: WireDoc }">
                        <div class="flex flex-col gap-1">
                            <span class="font-medium">{{ option.title }}</span>
                            <div class="flex items-center gap-2 text-sm text-ds-primary-600">
                                <span>{{ option.branch }}</span>
                            </div>
                        </div>
                    </template>
                </DsMultiSelect>

                <h4 class="text-sm text-content-secondary">Without Search</h4>
                <DsMultiSelect
                    v-model="selectedWireDocsWithoutSearch"
                    id="wire-docs-without-search"
                    :disableSelectAll
                    groupLabel="branch"
                    :label="labelDocuments"
                    :options="wireDocWithoutSearchOptions"
                    :placeholder="placeholderDocuments"
                    :showSelectAll
                >
                    <template #optionLabel="{ option }: DsMultiSelectSlotProps<WireDoc>">
                        <div class="flex flex-col gap-1">
                            <span class="font-medium">{{ option.title }}</span>
                        </div>
                    </template>

                    <template #chipContent="{ option, truncate }">
                        <div class="flex flex-col gap-1">
                            <span class="font-medium">{{ truncate(`custom chip content - ${option.title}`) }}</span>
                        </div>
                    </template>
                </DsMultiSelect>
            </div>
        </section>

        <section>
            <h3 class="mb-4 text-content-lg font-medium">Disabled Options</h3>
            <div class="flex flex-col gap-4">
                <h4 class="text-sm text-content-secondary">With Disabled Options</h4>
                <DsMultiSelect
                    v-model="selectedDisabledClients"
                    id="disabled-clients"
                    :disableSelectAll
                    helperText="Some clients are disabled and cannot be selected"
                    :label="labelClients"
                    :options="disabledClientOptions"
                    :placeholder="placeholderClients"
                    :showSelectAll
                >
                    <template #optionLabel="{ option }: DsMultiSelectSlotProps<Client>">
                        <div class="flex flex-col">
                            <div class="flex items-center gap-2 break-all">
                                <span class="text-ds-primary-600">{{ option.role }}:</span>
                                <span class="font-medium">{{ option.name }}</span>
                                <span
                                    v-if="option.disabled"
                                    class="text-xs text-ds-red-500"
                                >
                                    (Disabled)
                                </span>
                            </div>
                            <div class="break-all text-sm text-ds-primary-600">{{ option.email }}, {{ option.phone }}</div>
                        </div>
                    </template>
                </DsMultiSelect>

                <h4 class="text-sm text-content-secondary">Grouped with Disabled Options</h4>
                <DsMultiSelect
                    v-model="selectedDisabledWireDocs"
                    id="disabled-wire-docs"
                    :disableSelectAll
                    groupLabel="branch"
                    helperText="Some documents are disabled and cannot be selected"
                    :label="labelDocuments"
                    :options="disabledWireDocOptions"
                    :placeholder="placeholderDocuments"
                    :showSelectAll
                >
                    <template #optionLabel="{ option }: DsMultiSelectSlotProps<WireDoc>">
                        <div class="flex flex-col gap-1">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ option.title }}</span>
                                <span
                                    v-if="option.disabled"
                                    class="text-xs text-ds-red-500"
                                >
                                    (Disabled)
                                </span>
                            </div>
                            <div class="flex items-center gap-2 text-sm text-ds-primary-600">
                                <span>{{ option.branch }}</span>
                            </div>
                        </div>
                    </template>
                </DsMultiSelect>

                <DsMultiSelect
                    v-model="selectedClientsDisabled"
                    id="clients-disabled"
                    :disableSelectAll
                    disabled
                    :label="labelClientsDisabled"
                    :options="clientOptionsDisabled"
                    :placeholder="placeholderClientsDisabled"
                    :showSelectAll
                >
                    <template #optionLabel="{ option }: DsMultiSelectSlotProps<Client>">
                        <div class="flex flex-col">
                            <div class="flex items-center gap-2 break-all">
                                <span class="text-ds-primary-600">{{ option.role }}:</span>
                                <span class="font-medium">{{ option.name }}</span>
                            </div>
                            <div class="break-all text-sm text-ds-primary-600">{{ option.email }}, {{ option.phone }}</div>
                        </div>
                    </template>
                </DsMultiSelect>
            </div>
        </section>

        <section>
            <h3 class="mb-4 text-content-lg font-medium">Grouped with Mixed Disabled States</h3>
            <div class="flex flex-col gap-4">
                <h4 class="text-sm text-content-secondary">Three Groups: None Disabled, Some Disabled, All Disabled</h4>
                <DsMultiSelect
                    v-model="selectedMixedDisabledDocs"
                    id="mixed-disabled-docs"
                    :disableSelectAll
                    groupLabel="branch"
                    helperText="Demonstrates different disabled states across groups"
                    :label="labelDocuments"
                    :options="mixedDisabledDocOptions"
                    :placeholder="placeholderDocuments"
                    :showSelectAll
                >
                    <template #optionLabel="{ option }: DsMultiSelectSlotProps<WireDoc>">
                        <div class="flex flex-col gap-1">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ option.title }}</span>
                                <span
                                    v-if="option.disabled"
                                    class="text-xs text-ds-red-500"
                                >
                                    (Disabled)
                                </span>
                            </div>
                            <div class="flex items-center gap-2 text-sm text-ds-primary-600">
                                <span>{{ option.branch }}</span>
                            </div>
                        </div>
                    </template>
                </DsMultiSelect>
            </div>
        </section>

        <section class="col-span-2">
            <h3 class="mb-4 text-content-lg font-medium">Empty State</h3>
            <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <h4 class="text-sm text-content-secondary">Default empty state</h4>
                    <DsMultiSelect
                        v-model="emptyStateModel"
                        id="empty-state-default"
                        :disableSelectAll
                        :label="labelClients"
                        :options="[]"
                        :placeholder="placeholderClients"
                        :showSelectAll
                    />
                </div>

                <div class="flex flex-col gap-2">
                    <h4 class="text-sm text-content-secondary">Custom empty state slot</h4>
                    <DsMultiSelect
                        v-model="emptyStateModel"
                        id="empty-state-custom"
                        :disableSelectAll
                        :label="labelClients"
                        :options="[]"
                        :placeholder="placeholderClients"
                        :showSelectAll
                    >
                        <template #emptyState>
                            <DsSelectEmptyState
                                icon="fa-regular fa-user-group"
                                subtitle="Once added, all clients will be listed here."
                                title="No clients added yet"
                            >
                                <DsButton
                                    leading="fa-regular fa-user-plus"
                                    size="sm"
                                    variant="tertiary"
                                >
                                    Add clients
                                </DsButton>
                            </DsSelectEmptyState>
                        </template>
                    </DsMultiSelect>
                </div>
            </div>
        </section>

        <div class="flex w-fit flex-col gap-4">
            <h3 class="text-content-lg font-medium">Phone Number Filter Example</h3>
            <DsMultiSelect
                v-model="selectedPhoneNumbers"
                id="phone-numbers"
                :disableSelectAll
                groupLabel="branch"
                :isFullWidth="false"
                :options="phoneNumberOptions"
                placeholder="Filter by phone number"
                searchInputPlaceholder="Search phone numbers"
                :showSelectAll
            >
                <template #trigger="{ isOpen }">
                    <DsButton
                        size="sm"
                        :trailing="isOpen ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"
                        variant="secondary"
                    >
                        <template #leading>
                            Phone Number {{ selectedPhoneNumbers.length > 0 ? `(${selectedPhoneNumbers.length})` : '' }}
                        </template>
                    </DsButton>
                </template>

                <template #optionLabel="{ option }: DsMultiSelectSlotProps<PhoneNumber>">
                    <div class="flex flex-col">
                        <span class="font-medium">{{ option.phoneNumber }}</span>
                        <span class="text-sm text-ds-primary-500">{{ option.branch }}</span>
                    </div>
                </template>
            </DsMultiSelect>
        </div>

        <div class="flex w-fit flex-col gap-4">
            <h3 class="text-content-lg font-medium">Options with tooltip examples</h3>
            <DsMultiSelect
                v-model="selectedDocumentsWithTooltips"
                id="documents-with-labels"
                :disableSelectAll
                :isFullWidth="false"
                :options="documentsWithTooltips"
                placeholder="Filter documents"
                :showSelectAll
            />
        </div>

        <div class="flex flex-col gap-4">
            <h3 class="text-content-lg font-semibold">Modal Example</h3>
            <DsButton
                size="md"
                variant="primary"
                @click="showModal = true"
            >
                Open Modal with Multiselect
            </DsButton>
        </div>

        <DsDialog
            v-model:open="editClientDialog"
            primaryAction="Save"
            secondaryAction="Cancel"
            title="Edit Client"
            @clickPrimary="editClientDialog = false"
            @clickSecondary="editClientDialog = false"
        >
            <p>This is a modal for editing a client.</p>
        </DsDialog>

        <DsDialog
            v-model:open="showModal"
            primaryAction="Confirm Selection"
            secondaryAction="Cancel"
            title="Select Documents"
            @clickPrimary="handleModalSubmit"
            @clickSecondary="showModal = false"
        >
            <DsMultiSelect
                v-model="modalDocuments"
                id="documents-123"
                :disableSelectAll
                :error="modalDocumentsError"
                helperText="Select all documents you want to include in this package"
                label="Documents"
                :options="documentOptions"
                placeholder="Choose documents to include"
                :required="true"
                :showSelectAll
                @close="handleModalDocumentsClose"
            />
        </DsDialog>

        <div class="flex flex-col gap-4">
            <h3 class="text-content-lg font-semibold">Sheet Example</h3>
            <DsButton
                size="md"
                variant="primary"
                @click="showSheet = true"
            >
                Open Sheet with Multiselect
            </DsButton>
        </div>

        <DsSheet
            v-model:open="showSheet"
            primaryAction="Apply Actions"
            secondaryAction="Cancel"
            title="Bulk Actions"
            @clickPrimary="handleSheetSubmit"
            @clickSecondary="showSheet = false"
        >
            <div class="space-y-4">
                <DsMultiSelect
                    v-model="sheetClients"
                    id="clients-123"
                    :disableSelectAll
                    :error="sheetClientsError"
                    helperText="Select multiple clients to apply the same action to all of them"
                    label="Select Clients"
                    :options="clientOptions"
                    placeholder="Choose clients for bulk action"
                    required
                    :showSelectAll
                    @close="handleSheetClientsClose"
                />

                <DsMultiSelect
                    v-model="sheetActions"
                    id="actions-123"
                    :disableSelectAll
                    :error="sheetActionsError"
                    helperText="Select one or more actions to apply to the selected clients"
                    label="Select Actions"
                    :options="actionOptions"
                    placeholder="Choose actions to perform"
                    required
                    :showSelectAll
                    @close="handleActionsClose"
                />
            </div>
        </DsSheet>
    </div>
</template>

<script setup lang="ts">
import DsDialog from '@/components/design-system/DsDialog.vue';
import DsSelectEmptyState from '@/components/design-system/DsSelectEmptyState.vue';
import DsSheet from '@/components/design-system/DsSheet.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import type { DsMultiSelectSlotProps } from '@/components/design-system/multiselect/multi-select.types';
import { debounce } from 'lodash';
import { computed, ref, watch } from 'vue';

interface Client {
    id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
    disabled?: boolean;
}

interface Document {
    id: string;
    title: string;
}

interface WireDoc {
    id: string;
    title: string;
    branch: string;
    disabled?: boolean;
}

interface PhoneNumber {
    id: string;
    phoneNumber: string;
    branch: string;
}

const showSelectAll = ref(true);
const disableSelectAll = ref(false);

const labelClients = ref('Clients');
const placeholderClients = ref('Select clients');
const errorClients = ref('');

const handleClientsClose = () => {
    if (selectedClients.value.length === 0) {
        errorClients.value = 'Clients are required';
    } else {
        errorClients.value = '';
    }
};

const labelDocuments = ref('Documents');
const placeholderDocuments = ref('Select documents');
const errorDocuments = ref('');

const labelClientsDisabled = ref('Clients');
const placeholderClientsDisabled = ref('Select clients');

const handleDocumentsClose = () => {
    if (selectedDocuments.value.length === 0) {
        errorDocuments.value = 'Documents are required';
    } else {
        errorDocuments.value = '';
    }
};

const clients = ref<Client[]>([
    {
        id: '1',
        name: 'John Doe',
        email: 'john@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
    },
    {
        id: '2',
        name: 'Jane Smith',
        email: 'jane@example.com',
        phone: '(098) 765-4321',
        role: 'Seller',
    },
    {
        id: '3',
        name: 'Liza Strong',
        email: 'liza@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
    },
    {
        id: '4',
        name: 'Sally Seller',
        email: 'sally@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
    },
    {
        id: '5',
        name: 'Bilbo Baggins',
        email: 'bilbo@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
    },
    {
        id: '6',
        name: 'Samwise Gamgee',
        email: 'sam@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
    },
]);

const clientOptions = computed(() => {
    return clients.value.map(client => ({
        ...client,
        id: client.id,
        value: client.id,
        label: client.name,
    }));
});

const disabledClients = ref<Client[]>([
    {
        id: '1',
        name: 'John Doe',
        email: 'john@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
        disabled: false,
    },
    {
        id: '2',
        name: 'Jane Smith',
        email: 'jane@example.com',
        phone: '(098) 765-4321',
        role: 'Seller',
        disabled: true,
    },
    {
        id: '3',
        name: 'Liza Strong',
        email: 'liza@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
        disabled: false,
    },
    {
        id: '4',
        name: 'Sally Seller',
        email: 'sally@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
        disabled: true,
    },
    {
        id: '5',
        name: 'Bilbo Baggins',
        email: 'bilbo@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
        disabled: false,
    },
    {
        id: '6',
        name: 'Samwise Gamgee',
        email: 'sam@example.com',
        phone: '(123) 456-7890',
        role: 'Buyer',
        disabled: true,
    },
]);

const disabledClientOptions = computed(() => {
    return disabledClients.value.map(client => ({
        ...client,
        id: client.id,
        value: client.id,
        label: client.name,
    }));
});

const documents = ref<Document[]>([
    {
        id: '1',
        title: 'Wire_docs_contract_American_Title_Company_2025.pdf',
    },
    {
        id: '2',
        title: 'Closing_docs_100_Congress_Ave.pdf',
    },
    {
        id: '3',
        title: 'Buyer_agent_instructions_wire_documents.pdf',
    },
    {
        id: '4',
        title: 'Seller_agent_instructions_wire_documents.pdf',
    },
]);

const documentOptions = computed(() => {
    return documents.value.map(document => ({
        ...document,
        id: document.id,
        value: document.id,
        label: document.title,
    }));
});

const wireDocs = ref<WireDoc[]>([
    {
        id: '1',
        title: 'Wire_docs_contract_American_Title_Company_2025.pdf',
        branch: 'Branch 1',
    },
    {
        id: '2',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 1',
    },
    {
        id: '3',
        title: 'Post_closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 1',
    },
    {
        id: '7',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 2',
    },
    {
        id: '8',
        title: 'Post_closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 2',
    },
    {
        id: '13',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '14',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '15',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 2',
    },
    {
        id: '16',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '17',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '18',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
]);

const wireDocOptions = computed(() => {
    return wireDocs.value.map(wireDoc => ({
        ...wireDoc,
        id: wireDoc.id,
        value: wireDoc.id,
        label: wireDoc.title,
    }));
});

const disabledWireDocs = ref<WireDoc[]>([
    {
        id: '1',
        title: 'Wire_docs_contract_American_Title_Company_2025.pdf',
        branch: 'Branch 1',
        disabled: false,
    },
    {
        id: '2',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 1',
        disabled: true,
    },
    {
        id: '3',
        title: 'Post_closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 1',
        disabled: false,
    },
    {
        id: '7',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 2',
        disabled: true,
    },
    {
        id: '8',
        title: 'Post_closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 2',
        disabled: false,
    },
    {
        id: '13',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
        disabled: true,
    },
    {
        id: '14',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
        disabled: false,
    },
]);

const disabledWireDocOptions = computed(() => {
    return disabledWireDocs.value.map(wireDoc => ({
        ...wireDoc,
        id: wireDoc.id,
        value: wireDoc.id,
        label: wireDoc.title,
    }));
});

const clientOptionsDisabled = computed(() => {
    return clients.value.map(client => ({
        ...client,
        id: client.id,
        value: client.id,
        label: client.name,
    }));
});

const wireDocsWithoutSearch = ref<WireDoc[]>([
    {
        id: '1',
        title: 'Wire_docs_contract_American_Title_Company_2025.pdf',
        branch: 'Branch 1',
    },
    {
        id: '2',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 2',
    },
    {
        id: '3',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '4',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '5',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '6',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '7',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '8',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '9',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '10',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '11',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '12',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '13',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
    {
        id: '14',
        title: 'Closing_docs_100_Congress_Ave.pdf',
        branch: 'Branch 3',
    },
]);

const wireDocWithoutSearchOptions = computed(() => {
    return wireDocsWithoutSearch.value.map(wireDoc => ({
        ...wireDoc,
        id: wireDoc.id,
        value: wireDoc.id,
        label: wireDoc.title,
    }));
});

const phoneNumbers = ref<PhoneNumber[]>([
    {
        id: '1',
        phoneNumber: '(000) 000-0000',
        branch: 'South Austin Branch',
    },
    {
        id: '2',
        phoneNumber: '(000) 000-0000',
        branch: 'South Austin Branch',
    },
    {
        id: '3',
        phoneNumber: '(000) 000-0000',
        branch: 'South Austin Branch',
    },
    {
        id: '4',
        phoneNumber: '(555) 123-4567',
        branch: 'North Austin Branch',
    },
    {
        id: '5',
        phoneNumber: '(555) 987-6543',
        branch: 'North Austin Branch',
    },
    {
        id: '6',
        phoneNumber: '(512) 555-0123',
        branch: 'Downtown Branch',
    },
    {
        id: '7',
        phoneNumber: '(512) 555-0456',
        branch: 'Downtown Branch',
    },
    {
        id: '8',
        phoneNumber: '(512) 555-0789',
        branch: 'West Austin Branch',
    },
]);

const phoneNumberOptions = computed(() => {
    return phoneNumbers.value.map(phone => ({
        ...phone,
        id: phone.id,
        value: phone.id,
        label: phone.phoneNumber,
    }));
});

const mixedDisabledDocs = ref<WireDoc[]>([
    {
        id: 'mixed-1',
        title: 'Active Document 1',
        branch: 'Active Branch',
        disabled: false,
    },
    {
        id: 'mixed-2',
        title: 'Active Document 2',
        branch: 'Active Branch',
        disabled: false,
    },
    {
        id: 'mixed-3',
        title: 'Active Document 3',
        branch: 'Active Branch',
        disabled: false,
    },
    {
        id: 'mixed-4',
        title: 'Mixed Document 1',
        branch: 'Mixed Branch',
        disabled: false,
    },
    {
        id: 'mixed-5',
        title: 'Mixed Document 2',
        branch: 'Mixed Branch',
        disabled: true,
    },
    {
        id: 'mixed-6',
        title: 'Mixed Document 3',
        branch: 'Mixed Branch',
        disabled: false,
    },
    {
        id: 'mixed-7',
        title: 'Mixed Document 4',
        branch: 'Mixed Branch',
        disabled: true,
    },
    {
        id: 'mixed-8',
        title: 'Disabled Document 1',
        branch: 'Disabled Branch',
        disabled: true,
    },
    {
        id: 'mixed-9',
        title: 'Disabled Document 2',
        branch: 'Disabled Branch',
        disabled: true,
    },
    {
        id: 'mixed-10',
        title: 'Disabled Document 3',
        branch: 'Disabled Branch',
        disabled: true,
    },
]);

const documentsWithTooltips = computed(() => {
    return mixedDisabledDocs.value.map(doc => ({
        ...doc,
        value: doc.id,
        label: doc.title,
        tooltipText: `${doc.title} is now available for download and will expire in 30 days.`,
    }));
});

const mixedDisabledDocOptions = computed(() => {
    return mixedDisabledDocs.value.map(wireDoc => ({
        ...wireDoc,
        id: wireDoc.id,
        value: wireDoc.id,
        label: wireDoc.title,
    }));
});

const selectedClients = ref<Client[]>([]);
const clientSearch = ref('Pre-filled search query');
const documentsSearch = ref('wire');
const selectedDocuments = ref<Document[]>([]);
const selectedWireDocs = ref<WireDoc[]>([]);
const selectedWireDocsWithoutSearch = ref<WireDoc[]>([]);
const selectedPhoneNumbers = ref<PhoneNumber[]>([]);
const selectedDocumentsWithTooltips = ref<WireDoc[]>([]);

const selectedDisabledClients = ref<Client[]>([]);
const selectedDisabledWireDocs = ref<WireDoc[]>([]);
const selectedMixedDisabledDocs = ref<WireDoc[]>([]);

const selectedClientsDisabled = ref<Client[]>([]);

const emptyStateModel = ref([]);

const editClientDialog = ref(false);
const showModal = ref(false);
const showSheet = ref(false);

const debouncedSearchHandler = debounce((query: string) => {
    console.log('Debounced search:', query);
}, 300);

watch(clientSearch, newQuery => {
    debouncedSearchHandler(newQuery);
});

const handleToggleOption = (payload: { option: WireDoc; selected: boolean }) => {
    console.log('toggleOption', payload);
};

const handleOpen = () => {
    console.log('open');
};

const handleClose = () => {
    console.log('close');
};

const handleDocumentSearch = (query: string) => {
    debouncedSearchHandler(query);
};

const modalDocuments = ref<Document[]>([]);
const sheetClients = ref<Client[]>([]);
const sheetActions = ref<Action[]>([]);

const modalDocumentsError = ref('');
const sheetClientsError = ref('');
const sheetActionsError = ref('');

const handleModalDocumentsClose = () => {
    if (modalDocuments.value.length === 0) {
        modalDocumentsError.value = 'Please select at least one document';
        return;
    }

    modalDocumentsError.value = '';
};

const handleModalSubmit = () => {
    if (modalDocuments.value.length === 0) {
        modalDocumentsError.value = 'Please select at least one document';
        return;
    }

    modalDocumentsError.value = '';
    showModal.value = false;
};

interface Action {
    id: string;
    label: string;
    value: string;
}

const actionOptions: Action[] = [
    {
        id: 'action1',
        label: 'Send Email',
        value: 'email',
    },
    {
        id: 'action2',
        label: 'Generate Report',
        value: 'report',
    },
    {
        id: 'action3',
        label: 'Update Status',
        value: 'status',
    },
    {
        id: 'action4',
        label: 'Archive',
        value: 'archive',
    },
];

const handleActionsClose = () => {
    if (sheetActions.value.length === 0) {
        sheetActionsError.value = 'Please select at least one action';
    } else {
        sheetActionsError.value = '';
    }
};

const handleSheetClientsClose = () => {
    if (sheetClients.value.length === 0) {
        sheetClientsError.value = 'Please select at least one client';
    } else {
        sheetClientsError.value = '';
    }
};

const handleSheetSubmit = () => {
    let hasErrors = false;

    if (sheetClients.value.length === 0) {
        sheetClientsError.value = 'Please select at least one client';
        hasErrors = true;
    } else {
        sheetClientsError.value = '';
    }

    if (sheetActions.value.length === 0) {
        sheetActionsError.value = 'Please select at least one action';
        hasErrors = true;
    } else {
        sheetActionsError.value = '';
    }

    if (hasErrors) {
        return;
    }

    showSheet.value = false;
};
</script>
